import { CryptocoinService } from "@main-services/currency/cryptocoin.service";

import { Contact, ContactField, Cryptocoin, ValidationError } from "@models";

import { BpModalCanDismiss } from "@models/interfaces/bp-modal-can-dismiss";
import { UserService } from "../../services/user.service";
import { Helper } from "@helpers/functions";
import { ContactAddSuccessComponent } from "./contact-add-success/contact-add-success.component";
import { Directive, inject } from "@angular/core";
import { SendTransaction } from "@models/transaction";
import { ContactService } from "@main-services/contact.service";
import { CustomModal } from "../../../core/components/base-modals";
import { InfoService } from "@core-services/info.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { take } from "rxjs/operators";
import { TranslationService } from "@core-services/translation.service";
import { SendingModalRemoteService } from "@modules/main/components/step-summary/sending-modal/services/sending-modal-remote.service";

@Directive()
export abstract class ContactComponent extends CustomModal implements BpModalCanDismiss {
	public data: Contact;
	private sendingModalRemote = inject(SendingModalRemoteService);

	public modalCanDismiss(): boolean {
		return true;
	}

	public types: BpSelectOption[] = this.coinService.list
		.filter((c) => !c.isIndexOnly())
		.map((coin) => ({
			name: coin.getName(),
			iconUrl: coin.getLogo(),
			value: coin.getSymbol(true) + "_address"
		}));

	public constructor(
		protected contactService: ContactService,
		protected infoService: InfoService,
		protected userService: UserService,
		protected coinService: CryptocoinService,
		protected sendEntryPoint: SendEntryPointService,
		protected translationService: TranslationService
	) {
		super();
	}

	public save(): void {
		this.data.setFields(
			this.data
				.getFields()
				.filter((f) => !!f.getValue())
				.map((f) => {
					if (!f.getDestinationTag() || f.getDestinationTag().trim() === "") {
						f.setDestinationTag("");
					}
					return f;
				})
		);
		this.contactService.upsertCall(this.data).subscribe(
			() => {
				if (this.data.getId()) {
					this.closeAll(true);
				} else {
					this.modalService.showModal(ContactAddSuccessComponent, this.data, { backdropDismiss: false });
				}
			},
			(error) => {
				Helper.handleError(error, this.data);

				// For missing address show an error toast - inline error is not possible
				if (error instanceof ValidationError && error.metaContains("fields")) {
					Helper.infoService.setError(error);
				}
			}
		);
	}

	public getValidationErrorForField(field: ContactField): string {
		if (
			this.data
				.getValidationListError("fields")
				.items.some((item) => field.getType() === item.type && field.getValue() === item.value)
		) {
			return this.data.getValidationError("fields");
		}
		return "";
	}

	public getIconByAddress(address: string): string {
		const match = this.types.find((t) => t.value === address);
		return match ? match.iconUrl : "";
	}

	public async send(c: Contact, selectedAddress: ContactField = null, walletGuid: string = null): Promise<void> {
		const tx = new SendTransaction();
		tx.convertTx(c);
		if (selectedAddress) {
			tx.setRecipient(selectedAddress.getValue());
			tx.setCurrency(this.coinService.findBySymbol(selectedAddress.getSymbol()) || new Cryptocoin());
		}
		const coin = this.coinService.findBySymbol(selectedAddress.getSymbol());
		const sendStatus = await this.sendEntryPoint.getSendStatusForCryptoCoin$(coin).pipe(take(1)).toPromise();

		if (sendStatus.isDisabled) {
			if (sendStatus.message === "action_send_tooltip") {
				this.sendingModalRemote
					.fetchPriceLimits("external", walletGuid)
					.pipe(take(1))
					.subscribe((price) => {
						this.infoService.setError(
							this.translationService.translate(sendStatus.message, [
								price.getMinAmountExternal().tradableAmount() + " " + coin.getSymbol()
							])
						);
					});
				return;
			}
			this.infoService.setError(this.translationService.translate(sendStatus.message));
			return;
		}

		this.sendEntryPoint.openCryptoSendModal(
			{ transaction: tx },
			{
				perfectInputMatch: true,
				forceHideFiatSend: true
			}
		);
	}
}
